import {
  AfterViewInit,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AppModelModalComponent } from '@nimmsta/ng-commons-lib/forms';
import {
  Column,
  DataTableComponent,
  EmptyTableComponent,
  IconAction,
  NimmstaDataSource,
} from '@nimmsta/ng-commons-lib/data-table';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { NimmstaTimeAgoPipe } from '@nimmsta/ng-commons-lib/pipes';
import { EmailFormComponent } from './email-form.component';
import { WorkflowEmail } from './workflow-email';
import { WorkflowEmailService } from './workflow-email.service';

_('workflow-email.title');

@Component({
  selector: 'app-email',
  templateUrl: './email.component.html',
  imports: [
    EmailFormComponent,
    TranslatePipe,
    HeaderBarComponent,
    DataTableComponent,
    EmptyTableComponent,
    ErrorComponent,
    AppModelModalComponent,
  ],
  providers: [NimmstaTimeAgoPipe],
})
export class EmailComponent implements OnInit, AfterViewInit {
  private service = inject(WorkflowEmailService);
  private translate = inject(TranslateService);
  private timeago = inject(NimmstaTimeAgoPipe);

  source!: NimmstaDataSource<WorkflowEmail>;

  @ViewChild(DataTableComponent, { static: true })
  private tableComponent!: DataTableComponent<WorkflowEmail>;

  @ViewChild('EditModal', { static: true })
  private editModalComponent!: AppModelModalComponent<WorkflowEmail>;

  @ViewChild(EmailFormComponent, { static: true })
  protected formComponent!: EmailFormComponent;

  @ViewChild('errorDeleteModal', { static: true })
  errorDeleteModal!: TemplateRef<unknown>;

  iconActions: IconAction<WorkflowEmail>[] = [];

  error?: HttpErrorResponse;

  protected testEmailButtonEnabled = true;

  get columns(): Column[] {
    return [
      new Column('title', 'workflow-email.name', { sortable: true }),
      new Column('subject', 'workflow-email.subject', { sortable: true }),
      new Column('lastUpdatedAt', 'lastUpdatedAt', {
        pipe: this.timeago,
        sortable: true,
        invertSorting: true,
      }),
    ];
  }

  ngOnInit(): void {
    this.source = new NimmstaDataSource<WorkflowEmail>(this.service);
    const editAction = new IconAction('edit', 'edit');
    this.iconActions.push(editAction);
    editAction.subscribe((id) => this.edit(id));
    const deleteAction = new IconAction('delete', 'delete', true);
    this.iconActions.push(deleteAction);
    deleteAction.subscribe((id) => this.delete(id));
  }

  ngAfterViewInit(): void {
    this.formComponent.validityChange.subscribe(() => {
      this.testEmailButtonEnabled = this.formComponent.form.valid;
    });
  }

  show(id: string): void {
    this.translate.get('workflow-email.edit').subscribe((value) => {
      this.openModal(value, true, this.getSelectedObject(id)!);
    });
  }

  new(): void {
    this.translate.get('workflow-email.new').subscribe((value) => {
      const workflow = {} as WorkflowEmail;
      this.openModal(value, true, workflow);
    });
  }

  edit(id: string): void {
    this.translate.get('workflow-email.edit').subscribe((value) => {
      this.openModal(value, true, this.getSelectedObject(id)!);
    });
  }

  openModal(title: string, editable: boolean, value: WorkflowEmail): void {
    this.formComponent.editable = editable;
    this.formComponent.setValue(value);
    this.editModalComponent.title = title;
    this.editModalComponent.editable = editable;
    this.editModalComponent.show('xl');
  }

  delete(id: string): void {
    this.service.delete(this.getSelectedObject(id)!).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  onSave(): void {
    this.formComponent.onSave();
  }

  onModalClose(): void {
    this.editModalComponent.hide();
    this.tableComponent.loadDataPage();
  }

  getSelectedObject(id: string): WorkflowEmail | undefined {
    return this.source.data?.content.filter((d) => id === d.id).pop();
  }

  onLoading(loading: boolean): void {
    this.editModalComponent.loading = loading;
  }

  onTest() {
    this.formComponent.testEmail();
  }
}
